export const FIREBASE_CONFIG = {
  apiKey: "AIzaSyD5nGXicQ2TrF2f59b8be-dSblKOPuJAnU",
  authDomain: "omnia-kingdom-vault.firebaseapp.com",
  projectId: "omnia-kingdom-vault",
  storageBucket: "omnia-kingdom-vault-storage",
  messagingSenderId: "178720940718",
  appId: "1:178720940718:web:a679954cbc115c9c71833f",
};

/**
 * Official Kingdom Gateway Allowlist.
 * As commanded by Emperor Ryan:
 * - ryan@omniatheatre.com
 * - bill@omniatheatre.com
 * - ptolemy@omniatheatre.com
 */
export const KINGDOM_GATEWAY_ALLOWLIST = Object.freeze([
  "ryan@omniatheatre.com",
  "bill@omniatheatre.com",
  "ptolemy@omniatheatre.com",
]);

let authInitialized = false;

// Dynamic load from Google official ESM CDN — zero bundle bloat and zero node_modules churn
async function getFirebaseSdk() {
  if (typeof window === "undefined") return null;
  try {
    const appMod = await import(
      /* @vite-ignore */ "https://www.gstatic.com/firebasejs/10.12.0/firebase-app.js"
    );
    const authMod = await import(
      /* @vite-ignore */ "https://www.gstatic.com/firebasejs/10.12.0/firebase-auth.js"
    );

    const app =
      appMod.getApps().length === 0
        ? appMod.initializeApp(FIREBASE_CONFIG)
        : appMod.getApps()[0];
    const auth = authMod.getAuth(app);
    return { auth, GoogleAuthProvider: authMod.GoogleAuthProvider, signInWithPopup: authMod.signInWithPopup, signOut: authMod.signOut, onAuthStateChanged: authMod.onAuthStateChanged };
  } catch (err) {
    console.error("[google-auth] Failed to load Firebase Auth SDK:", err);
    return null;
  }
}

export type GoogleAuthSession = {
  ok: boolean;
  token?: string;
  email?: string;
  name?: string;
  picture?: string | null;
  error?: string;
};

export async function signInWithGoogleOAuth(): Promise<GoogleAuthSession> {
  const sdk = await getFirebaseSdk();
  if (!sdk) {
    return { ok: false, error: "Unable to load Google authentication SDK. Please check your network connection." };
  }

  const { auth, GoogleAuthProvider, signInWithPopup, signOut } = sdk;
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  try {
    const cred = await signInWithPopup(auth, provider);
    const user = cred.user;
    const email = user.email?.toLowerCase().trim() ?? "";

    if (!KINGDOM_GATEWAY_ALLOWLIST.includes(email)) {
      await signOut(auth);
      return {
        ok: false,
        email,
        error: `Access Denied: '${email}' is not on the Kingdom Allowlist. Authorized accounts: ryan@, bill@, ptolemy@omniatheatre.com`,
      };
    }

    const idToken = await user.getIdToken(true);
    let googleIdToken: string | undefined;
    try {
      const googleCredential = GoogleAuthProvider.credentialFromResult(cred);
      if (googleCredential?.idToken) {
        googleIdToken = googleCredential.idToken;
      }
    } catch {
      // Fallback to idToken
    }

    // Exchange with gateway server
    const res = await fetch("/api/auth/google-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken, googleIdToken }),
    });

    if (!res.ok) {
      const errData = (await res.json().catch(() => ({}))) as { error?: string };
      await signOut(auth);
      return {
        ok: false,
        email,
        error: errData.error || `Server authorization failed (${res.status})`,
      };
    }

    const sessionData = (await res.json()) as {
      token: string;
      email?: string;
      name?: string;
      picture?: string | null;
    };

    return {
      ok: true,
      token: sessionData.token,
      email: sessionData.email || email,
      name: sessionData.name || user.displayName || email,
      picture: sessionData.picture || user.photoURL,
    };
  } catch (err: unknown) {
    const code = (err as { code?: string })?.code ?? "";
    if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
      return { ok: false, error: "Sign-in cancelled by user" };
    }
    if (code === "auth/popup-blocked") {
      return {
        ok: false,
        error: "Sign-in popup blocked by browser. Please allow popups for this site and try again.",
      };
    }
    if (code === "auth/unauthorized-domain") {
      return {
        ok: false,
        error: "Domain not authorized in Firebase Auth. Check authorized domains.",
      };
    }
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, error: `Authentication failed: ${message}` };
  }
}

export async function initGoogleAuthListener(
  onAuthenticated: (session: GoogleAuthSession) => void,
) {
  if (authInitialized || typeof window === "undefined") return;
  authInitialized = true;

  const sdk = await getFirebaseSdk();
  if (!sdk) return;

  const { auth, signOut, onAuthStateChanged } = sdk;

  onAuthStateChanged(auth, async (user: any) => {
    if (!user || !user.email) return;
    const email = user.email.toLowerCase().trim();
    if (!KINGDOM_GATEWAY_ALLOWLIST.includes(email)) {
      await signOut(auth);
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const res = await fetch("/api/auth/google-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      if (res.ok) {
        const sessionData = (await res.json()) as {
          token: string;
          email?: string;
          name?: string;
          picture?: string | null;
        };
        onAuthenticated({
          ok: true,
          token: sessionData.token,
          email: sessionData.email || email,
          name: sessionData.name || user.displayName || email,
          picture: sessionData.picture || user.photoURL,
        });
      }
    } catch {
      // Background exchange error, user can manually trigger
    }
  });
}
